/**
 * Webhook_Service.ts
 * Webhook subscription management + delivery retry  (sk-catalog-api /api/v1/webhooks)
 */

import axiosInstance from '../../api/axiosInstance';
import {
  Catalog_Webhooks_Url,
  Catalog_Webhook_Url,
  Catalog_Webhook_RotateSecret_Url,
  Catalog_Webhook_Deliveries_Url,
  Catalog_Webhook_DeliveryRetry_Url,
} from '../../api/endpoints';
import {
  CreateWebhookPayload,
  UpdateWebhookPayload,
  Webhook,
  WebhookResponse,
  WebhooksResponse,
} from '../../schemas/catalog/webhook';

/* ─────────────────────────────────────────
   List — GET /api/v1/webhooks
───────────────────────────────────────── */

export const getWebhooks = async (): Promise<WebhooksResponse> => {
  const { data } = await axiosInstance.get<WebhooksResponse>(Catalog_Webhooks_Url);
  return data;
};

/* ─────────────────────────────────────────
   Get one — GET /api/v1/webhooks/:id
───────────────────────────────────────── */

export const getWebhook = async (id: string): Promise<WebhookResponse> => {
  const { data } = await axiosInstance.get<WebhookResponse>(Catalog_Webhook_Url(id));
  return data;
};

/* ─────────────────────────────────────────
   Create — POST /api/v1/webhooks
───────────────────────────────────────── */

export const createWebhook = async (payload: CreateWebhookPayload): Promise<WebhookResponse> => {
  const { data } = await axiosInstance.post<WebhookResponse>(Catalog_Webhooks_Url, payload);
  return data;
};

/* ─────────────────────────────────────────
   Update — PATCH /api/v1/webhooks/:id
───────────────────────────────────────── */

export const updateWebhook = async (id: string, payload: UpdateWebhookPayload): Promise<WebhookResponse> => {
  const { data } = await axiosInstance.patch<WebhookResponse>(Catalog_Webhook_Url(id), payload);
  return data;
};

/* ─────────────────────────────────────────
   Rotate secret — POST /api/v1/webhooks/:id/rotate-secret
───────────────────────────────────────── */

export const rotateWebhookSecret = async (id: string): Promise<WebhookResponse> => {
  const { data } = await axiosInstance.post<WebhookResponse>(Catalog_Webhook_RotateSecret_Url(id));
  return data;
};

/* ─────────────────────────────────────────
   Delete — DELETE /api/v1/webhooks/:id
───────────────────────────────────────── */

export const deleteWebhook = async (id: string): Promise<void> => {
  await axiosInstance.delete(Catalog_Webhook_Url(id));
};

/* ─────────────────────────────────────────
   Delivery history — GET /api/v1/webhooks/:id/deliveries
───────────────────────────────────────── */

export const getDeliveries = async (id: string): Promise<{ items: any[] }> => {
  const { data } = await axiosInstance.get<{ items: any[] }>(Catalog_Webhook_Deliveries_Url(id));
  return data;
};

/* ─────────────────────────────────────────
   Retry a delivery — POST /api/v1/webhooks/:id/deliveries/:deliveryId/retry
───────────────────────────────────────── */

export const retryDelivery = async (id: string, deliveryId: string | number): Promise<any> => {
  const { data } = await axiosInstance.post(Catalog_Webhook_DeliveryRetry_Url(id, String(deliveryId)));
  return data;
};

export type { Webhook };
